import { map } from 'lodash'
import React, { useMemo } from 'react'
import { Field, Form, Formik } from 'formik'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, makeStyles } from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'

import { DraggablePaper, OutlinedButton, ContainedButton } from 'civic-champs-shared'
import { StyledInput } from 'civic-champs-shared/formik/components/StyledInput'
import { CkEditorStyledField } from 'civic-champs-shared/formik/components/CkEditorStyledField'
import { SuperEvent, SuperEventMember, superEventSchema } from 'super-events/types'
import { EventSelectField } from './EventSelectField'

interface AddEditSuperEventCanceled {
  proceed: false
}

interface AddEditSuperEventReady {
  proceed: true
  superEvent: SuperEvent
}

export type AddEditSuperEventResult = AddEditSuperEventCanceled | AddEditSuperEventReady

interface AddEditSuperEventPromptProps {
  showing: boolean
  complete: (result: AddEditSuperEventResult) => void
  superEvent?: SuperEvent
}

const useStyles = makeStyles({
  dialog: {
    background: '#F8FAFF',
    padding: '0 20px 20px',
    position: 'relative',
    maxWidth: '100%',
    width: '895px',
  },
  title: {
    cursor: 'move',
    padding: '20px 0 20px',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '24px',
    color: '#000000',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  closeIcon: {
    fontSize: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflowY: 'visible',
    textAlign: 'center',
    gap: '25px',
    '&:first-child': {
      padding: '10px 0 8px',
    },
  },
  alert: {
    margin: '12px 0 12px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0 0',
  },
})

export function AddEditSuperEventPrompt(props: AddEditSuperEventPromptProps) {
  const { showing, complete, superEvent: editedEvent } = props

  const initialValues = useMemo(() => {
    if (editedEvent) {
      return {
        title: editedEvent.title,
        description: editedEvent.description,
        //TODO kludge for split type
        events: map(editedEvent.events as SuperEventMember[], event => ({
          opportunityId: event.opportunityId,
          date: event.date,
        })),
      }
    } else {
      return {
        title: '',
        description: '',
        events: [],
      }
    }
  }, [editedEvent])

  const handleClose = () => {
    complete({ proceed: false })
  }

  const handleSubmit = (values: any) => {
    complete({
      proceed: true,
      superEvent: editedEvent ? { ...values, id: editedEvent.id } : values,
    })
  }

  const classes = useStyles()

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={superEventSchema}
      onSubmit={handleSubmit}
      isInitialValid={!!editedEvent}
      validateOnChange
    >
      {({ isSubmitting, submitForm }) => (
        <Form>
          <Dialog
            open={showing}
            PaperComponent={DraggablePaper}
            // @ts-ignore
            PaperProps={{ handle: '#add-edit-super-event-prompt' }}
            maxWidth="xs"
            classes={{ paper: classes.dialog }}
            disableEnforceFocus
          >
            <DialogTitle id="add-edit-super-event-prompt" classes={{ root: classes.title }} disableTypography={true}>
              {!!editedEvent ? 'Edit' : 'Add'} Super Event
              <IconButton className={classes.closeButton} onClick={handleClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </DialogTitle>
            <DialogContent className={classes.content}>
              <Field name="title" component={StyledInput} label={'Name *'} placeholder="Enter the Super Event's name" />
              <Field
                name="description"
                component={CkEditorStyledField}
                label={'Super Event Description *'}
                placeholder="Enter the Super Event's description"
              />
              <Field name="events" component={EventSelectField} />
            </DialogContent>
            <DialogActions className={classes.actions}>
              <OutlinedButton disabled={isSubmitting} onClick={handleClose}>
                Cancel
              </OutlinedButton>
              <ContainedButton disabled={isSubmitting} onClick={() => submitForm()}>
                Save
              </ContainedButton>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  )
}
